import { useMemo } from 'react';
import { pipe } from 'fp-ts/function';
import { array, number, ord, string } from 'fp-ts';
import { WithReservationId } from '@mews-commander/types';
import { OrderItem, OrderItemType, Product, ReservationResult } from '@mews-commander/data-access';
import { useLocalizeProperty } from '@mews/framework';
import { useReservationsSuspenseQuery } from '@mews-commander/legacy-entities/src/queries/reservations';
import { ProductCounts } from './types';

const extent = {
    orderItems: true,
    products: true,
};

const getOrderCount = (item: OrderItem) =>
    item.unitMultiplier * item.timeMultiplier * item.nightMultiplier * item.personMultiplier;

const productOrderingContramap = pipe(
    number.Ord,
    ord.contramap((product: Product) => product.ordering)
);

export type UseReservationProductOrdersParams = WithReservationId;

export const useReservationProductOrders = ({ reservationId }: UseReservationProductOrdersParams) => {
    const { data } = useReservationsSuspenseQuery<ReservationResult>({ reservationIds: [reservationId], extent });
    const localizeProperty = useLocalizeProperty();

    const products = useMemo(() => {
        if (!data.products) return [];

        return pipe(
            data.products,
            array.filter<Product>((product) => !product.isDefault),
            array.sortBy([
                productOrderingContramap,
                pipe(
                    string.Ord,
                    ord.contramap((product: Product) => localizeProperty(product.names))
                ),
            ])
        );
    }, [data.products, localizeProperty]);

    const counts = useMemo(() => {
        if (!data.orderItems) return {};

        return data.orderItems.reduce<ProductCounts>((acc, item) => {
            if (item.isCanceled || item.data.discriminator !== OrderItemType.ProductOrder) return acc;

            const { productId } = item.data.value;

            if (productId) {
                const count = getOrderCount(item);

                acc[productId] = (acc[productId] || 0) + count;
            }

            return acc;
        }, {});
    }, [data.orderItems]);

    const totalCount = useMemo(
        () =>
            products.reduce((acc, product) => {
                const count = counts[product.id] || 0;

                return acc + count;
            }, 0),
        [counts, products]
    );

    return { counts, products, totalCount };
};
